/* eslint-disable no-console */
/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { type Configuration, LogLevel } from '@azure/msal-browser';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const aadClientID = import.meta.env.VITE_AAD_CLIENT_ID;
const aadTenantID = import.meta.env.VITE_AAD_TENANT_ID;
const aadLoginRequestScope = ['user.read'];
const aadmlgraphRequestScope = import.meta.env.VITE_ML_GRAPH_SCOPE;
const aadRedirectUri = globalThis.location.origin;

export const msalConfig: Configuration = {
  auth: {
    clientId: `${aadClientID}`, // This is the ONLY mandatory field that you need to supply.
    authority: `https://login.microsoftonline.com/${aadTenantID}`, // Defaults to "https://login.microsoftonline.com/common"
    redirectUri: `${aadRedirectUri}`, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: `${aadRedirectUri}`, // Indicates the page to navigate after logout.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "localStorage" gives you SSO between tabs.
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning, // Configures logger verbosity
      loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: aadLoginRequestScope,
};

export const msGraphRequest = {
  scopes: aadLoginRequestScope,
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  graphql: {
    scopes: [aadmlgraphRequestScope], // e.g. api://xxxxxx/access_as_user
  },
};
